import Hammer, {
	type HammerManagerExtended,
	Manager,
	type RecognizerExtended,
} from 'hammerjs';
import {
	defineComponent,
	h,
	Plugin,
} from 'vue';

interface VueHammerConfig extends Record<HammerGesture, GuardDirectionsOptions>, Omit<RecognizerOptions, 'direction'> {
	direction?: HammerDirection;
	customEvents?: Record<string, HammerCustomEvent>;
}

type HammerRecognizerKey = 'Tap' | 'Pan' | 'Swipe' | 'Pinch' | 'Rotate' | 'Press';

type HammerDirectionkey = 'DIRECTION_NONE' | 'DIRECTION_LEFT' | 'DIRECTION_RIGHT' | 'DIRECTION_UP' | 'DIRECTION_DOWN' | 'DIRECTION_HORIZONTAL' | 'DIRECTION_VERTICAL' | 'DIRECTION_ALL';

type HammerDirection = 'up' | 'down' | 'left' | 'right' | 'horizontal' | 'vertical' | 'all';

type HammerGesture = 'tap' | 'pan' | 'swipe' | 'pinch' | 'rotate' | 'press';

interface HammerCustomEvent extends RecognizerOptions {
	type: HammerGesture;
}

interface GuardDirectionsOptions {
	direction: number | HammerDirection;
}

const gestures: HammerGesture[] = [
	'tap',
	'pan',
	'pinch',
	'press',
	'rotate',
	'swipe',
];
const subGestures = [
	'panstart',
	'panend',
	'panmove',
	'pancancel',
	'pinchstart',
	'pinchmove',
	'pinchend',
	'pinchcancel',
	'pinchin',
	'pinchout',
	'pressup',
	'rotatestart',
	'rotatemove',
	'rotateend',
	'rotatecancel',
];
const gestureMap: Record<string, HammerGesture> = {
	pan: 'pan',
	panstart: 'pan',
	panmove: 'pan',
	panend: 'pan',
	pancancel: 'pan',
	panleft: 'pan',
	panright: 'pan',
	panup: 'pan',
	pandown: 'pan',
	pinch: 'pinch',
	pinchstart: 'pinch',
	pinchmove: 'pinch',
	pinchend: 'pinch',
	pinchcancel: 'pinch',
	pinchin: 'pinch',
	pinchout: 'pinch',
	press: 'press',
	pressup: 'press',
	rotate: 'rotate',
	rotatestart: 'rotate',
	rotatemove: 'rotate',
	rotateend: 'rotate',
	rotatecancel: 'rotate',
	swipe: 'swipe',
	swipeleft: 'swipe',
	swiperight: 'swipe',
	swipeup: 'swipe',
	swipedown: 'swipe',
	tap: 'tap',
};
const directions: HammerDirection[] = [
	'up',
	'down',
	'left',
	'right',
	'horizontal',
	'vertical',
	'all',
];

function guardDirections(options: GuardDirectionsOptions): GuardDirectionsOptions {
	const dir = options.direction;

	if (typeof dir === 'string') {
		const hammerDirection = `DIRECTION_${dir.toUpperCase()}` as HammerDirectionkey;

		if (
			directions.indexOf(dir) > -1
			&& Object.prototype.hasOwnProperty.call(
				Hammer,
				hammerDirection,
			)
		) {
			options.direction = Hammer[hammerDirection];
		} else {
			console.warn(`[vue-hammer] invalid direction: ${dir}`);
		}
	}

	return options;
}

function buildEventWithDirections(
	eventName: string,
	directionArray: string[],
) {
	const f: Partial<Record<HammerDirection, number>> = {};
	(directionArray as HammerDirection[]).forEach((dir) => {
		dir = dir.toLowerCase() as HammerDirection;

		if (dir === 'horizontal') {
			f.left = 1;
			f.right = 1;
		} else if (dir === 'vertical') {
			f.up = 1;
			f.down = 1;
		} else if (dir === 'all') {
			f.left = 1;
			f.right = 1;
			f.up = 1;
			f.down = 1;
		} else {
			f[dir] = 1;
		}
	});
	const _directionArray = Object.keys(f);

	if (_directionArray.length === 0) {
		return eventName;
	}

	const eventWithDirArray = _directionArray.map((dir) => eventName + dir);

	return eventWithDirArray.join(' ');
}

function capitalize<T extends string>(str: string): T {
	return str.charAt(0).toUpperCase() + str.slice(1) as T;
}

const VueHammer: Plugin = {
	install(
		app,
		config: Partial<VueHammerConfig> = {},
	) {
		app.directive(
			'hammer',
			{
				beforeMount(el, binding) {
					if (!el.hammer) {
						el.hammer = new Hammer.Manager(el);
					}

					const mc = el.hammer;

					// determine event type
					const event = binding.arg;

					if (!event) {
						console.warn('[vue-hammer] event type argument is required.');
						return;
					}

					el.__hammerConfig = (
						el.__hammerConfig
						|| {}
					);
					el.__hammerConfig[event] = {};

					const direction = binding.modifiers;
					el.__hammerConfig[event].direction = (
						el.__hammerConfig[event].direction
						|| []
					);

					if (Object.keys(direction).length) {
						Object.keys(direction)
							.filter((keyName) => binding.modifiers[keyName])
							.forEach((keyName) => {
								const elDirectionArray = el.__hammerConfig[event].direction;

								if (elDirectionArray.indexOf(keyName) === -1) {
									elDirectionArray.push(String(keyName));
								}
							});
					}

					let recognizerType: HammerGesture | undefined;
					let recognizer: Recognizer;

					if (config.customEvents?.[event]) {
						// custom event
						const custom = config.customEvents[event];
						recognizerType = custom.type;
						const capitalizedType = capitalize<HammerRecognizerKey>(recognizerType);
						recognizer = new Hammer[capitalizedType](custom);
						recognizer.recognizeWith(mc.recognizers);
						mc.add(recognizer);
					} else {
						// built-in event
						recognizerType = gestures.find((gesture) => gesture === event);
						const subGesturesType = subGestures.find((gesture) => gesture === event);

						if (!recognizerType && !subGesturesType) {
							console.warn(`[vue-hammer] invalid event type: ${event}`);
							return;
						}

						if (
							subGesturesType
							&& el.__hammerConfig[subGesturesType].direction.length !== 0
						) {
							console.warn(`[vue-hammer] ${subGesturesType} should not have directions`);
						}

						if (!recognizerType) {
							return;
						}

						if (
							(
								recognizerType === 'tap'
								|| recognizerType === 'pinch'
								|| recognizerType === 'press'
								|| recognizerType === 'rotate'
							)
							&& el.__hammerConfig[recognizerType].direction.length !== 0
						) {
							throw Error(`[vue-hammer] ${recognizerType} should not have directions`);
						}

						recognizer = mc.get(recognizerType);

						if (!recognizer) {
							const capitalizedType = capitalize<HammerRecognizerKey>(recognizerType);
							// add recognizer
							recognizer = new Hammer[capitalizedType]();
							// make sure multiple recognizers work together...
							recognizer.recognizeWith(mc.recognizers);
							mc.add(recognizer);
						}

						// apply global options
						const globalOptions = config[recognizerType];

						if (globalOptions) {
							guardDirections(globalOptions);
							recognizer.set(globalOptions as RecognizerOptions);
						}

						// apply local options
						const localOptions: GuardDirectionsOptions = (
							el.hammerOptions
							&& el.hammerOptions[recognizerType]
						);

						if (localOptions) {
							guardDirections(localOptions);
							recognizer.set(localOptions as RecognizerOptions);
						}
					}
				},
				mounted(el, binding) {
					const mc = el.hammer;
					const event = binding.arg;

					if (event) {
						const eventWithDir = (
							subGestures.find((subGes) => subGes === event)
								? event
								: buildEventWithDirections(
									event,
									el.__hammerConfig[event].direction,
								)
						);

						if (mc.handler) {
							mc.off(
								eventWithDir,
								mc.handler,
							);
						}

						if (typeof binding.value !== 'function') {
							mc.handler = null;
							console.warn(`[vue-hammer] invalid handler function for v-hammer: ${binding.arg}`);
						} else {
							mc.on(
								eventWithDir,
								(mc.handler = binding.value),
							);
						}
					}
				},
				updated(el, binding) {
					const mc = el.hammer;
					const event = binding.arg;

					if (event) {
						const eventWithDir = (
							subGestures.find((subGes) => subGes === event)
								? event
								: buildEventWithDirections(
									event,
									el.__hammerConfig[event].direction,
								)
						);

						// teardown old handler
						if (mc.handler) {
							mc.off(
								eventWithDir,
								mc.handler,
							);
						}

						if (typeof binding.value !== 'function') {
							mc.handler = null;
							console.warn(`[vue-hammer] invalid handler function for v-hammer: ${binding.arg}`);
						} else {
							mc.on(
								eventWithDir,
								(mc.handler = binding.value),
							);
						}
					}
				},
				unmounted(el, binding) {
					const mc = el.hammer;
					const event = binding.arg;

					if (event) {
						const eventWithDir = (
							subGestures.find((subGes) => subGes === event)
								? event
								: buildEventWithDirections(
									event,
									el.__hammerConfig[event].direction,
								)
						);

						if (mc.handler) {
							el.hammer.off(
								eventWithDir,
								mc.handler,
							);
						}

						if (!Object.keys(mc.handlers).length) {
							el.hammer.destroy();
							el.hammer = null;
						}
					}
				},
			},
		);
	},
};

const VueTouch = defineComponent({
	name: 'VueTouch',
	props: {
		options: {
			default: () => ({}),
			type: Object,
		},
		tapOptions: {
			default: () => ({}),
			type: Object,
		},
		panOptions: {
			default: () => ({}),
			type: Object,
		},
		pinchOptions: {
			default: () => ({}),
			type: Object,
		},
		pressOptions: {
			default: () => ({}),
			type: Object,
		},
		rotateOptions: {
			default: () => ({}),
			type: Object,
		},
		swipeOptions: {
			default: () => ({}),
			type: Object,
		},
		tag: {
			default: 'div',
			type: String,
		},
		enabled: {
			default: true,
			type: [
				Boolean,
				Object,
			],
		},
	},
	emits: gestures,
	setup() {
		let hammer!: HammerManagerExtended;
		let recognizers!: Partial<Record<HammerGesture, RecognizerExtended>>;

		return {
			hammer,
			recognizers,
		};
	},
	watch: {
		enabled: {
			deep: true,
			handler(): void {
				this.updateEnabled(this.enabled);
			},
		},
	},
	beforeUnmount() {
		this.hammer.destroy();
	},
	mounted() {
		this.hammer = new Manager(
			this.$el,
			this.options,
		) as HammerManagerExtended;
		this.recognizers = {};
		this.setupBuiltinRecognizers();
		this.updateEnabled(this.enabled);
	},
	methods: {
		setupBuiltinRecognizers() {
			for (let i = 0; i < gestures.length; i += 1) {
				const gesture = gestures[i];
				const onGesture = `on${capitalize(gesture)}`;

				if (this.$.vnode.props?.[onGesture]) {
					const mainGesture = gestureMap[gesture];
					const mainGestureOptions = (this as any)[`${mainGesture}Options`] || {};
					const options: GuardDirectionsOptions = {
						...mainGestureOptions,
					};
					this.addRecognizer(
						mainGesture,
						options,
					);
					this.addEvent(gesture);
				}
			}
		},
		addRecognizer(
			gesture: HammerGesture,
			options: GuardDirectionsOptions,
			ref: { mainGesture?: 'Recognizer' | 'Tap' | 'Pan' | 'Swipe' | 'Pinch' | 'Rotate' | 'Press' } = {},
		): void {
			const { mainGesture } = ref;

			if (!this.recognizers[gesture]) {
				const typedGesture = capitalize(mainGesture || gesture) as 'Recognizer' | 'Tap' | 'Pan' | 'Swipe' | 'Pinch' | 'Rotate' | 'Press';
				const recognizer = new Hammer[typedGesture](guardDirections(options) as RecognizerOptions) as RecognizerExtended;
				this.recognizers[gesture] = recognizer;
				this.hammer.add(recognizer);
				recognizer.recognizeWith(this.hammer.recognizers);
			}
		},
		addEvent(gesture: HammerGesture): void {
			this.hammer.on(
				gesture,
				(event) => (
					this.$emit(
						gesture,
						event,
					)
				),
			);
		},
		updateEnabled(newVal: boolean | Record<HammerGesture, boolean>): void {
			if (newVal === true) {
				this.enableAll();
			} else if (newVal === false) {
				this.disableAll();
			} else if (typeof newVal === 'object') {
				const keys = Object.keys(newVal) as HammerGesture[];

				for (const event of keys) {
					if (this.recognizers[event]) {
						if (newVal[event]) {
							this.enable(event);
						} else {
							this.disable(event);
						}
					}
				}
			}
		},
		enable(r: HammerGesture): void {
			const recognizer = this.recognizers[r];

			if (
				recognizer
				&& !recognizer.options?.enable
			) {
				recognizer.set({
					enable: true,
				});
			}
		},
		disable(r: HammerGesture): void {
			const recognizer = this.recognizers[r];

			if (recognizer?.options.enable) {
				recognizer.set({
					enable: false,
				});
			}
		},
		toggle(r: HammerGesture): void {
			const recognizer = this.recognizers[r];

			if (recognizer?.options?.enable) {
				this.disable(r);
			} else {
				this.enable(r);
			}
		},
		enableAll(): void {
			this.toggleAll({
				enable: true,
			});
		},
		disableAll(): void {
			this.toggleAll({
				enable: false,
			});
		},
		toggleAll(ref: { enable: boolean; }): void {
			const { enable } = ref;
			const keys = Object.keys(this.recognizers) as HammerGesture[];

			for (const event of keys) {
				const recognizer = this.recognizers[event];

				if (
					recognizer
					&& recognizer.options.enable !== enable
				) {
					recognizer.set({
						enable,
					});
				}
			}
		},
		isEnabled(r: HammerGesture): boolean {
			return (
				this.recognizers[r]
				&& this.recognizers[r].options.enable
			);
		},
	},
	render() {
		return h(
			this.tag,
			{},
			this.$slots,
		);
	},
});

export { VueTouch };

export default VueHammer;
